import { addDays, addTally, handled, noTally, rate, type Route, type Tally } from '@day';
import { useStore } from '../app/store';
import { openCourier, openPerson, openRoute, openStop, openZones, planWith, ui } from '../app/ui';
import { ago, dayOf, dayWords, length, money, phoneMatches, plural, reasonWords, streetOf, time } from '../app/words';
import * as act from '../app/actions';
import { onCallNow, pausedNow, profileEdits, vehicleNow } from '../app/actions';
import { colourWords } from '../app/menus';
import { track } from '../data/analytics';
import { colourChoices, keyOf, routeTitle, type Model, type Person } from '../data/model';
import { Av, Dot, EmptyHelp, More } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { openDialog } from '../app/dialogs';
import { openWords, routeOfPerson, vehicleWords } from './Live';
import { handCell } from './LiveTables';
import { ZonesView } from './Zones';

/** Couriers: everyone in the business, with the picked one's record docked beside (control-room-web.html). */

/**
 * On a route, waiting to start, finished one today, paused (and not out since), or not out yet (not on a route and none
 * finished today).
 */
export function courierState(m: Model, p: Person): { k: 'running' | 'waiting' | 'finished' | 'paused' | 'notOut'; text: string; chip: React.ReactNode } {
  const run = p.day?.running;
  const wait = p.day?.waiting[0];
  const done = p.day?.finished[0];
  if (run)
    return {
      k: 'running',
      text: `On a route · ${routeTitle(run, m.zone)}`,
      chip: (
        <span className="chip">
          <Dot p={p} />
          On a route · {routeTitle(run, m.zone)}
        </span>
      ),
    };
  if (wait)
    return {
      k: 'waiting',
      text: `Waiting to start · ${routeTitle(wait, m.zone)}`,
      chip: (
        <span className="chip">
          <Icon name="circleDashed" size="sm" />
          Waiting to start · {routeTitle(wait, m.zone)}
        </span>
      ),
    };
  if (done) {
    const text = `${done.state === 'ended' ? 'Ended' : 'Finished'} ${time(done.completedAt ?? done.endedAt, m.zone)}`;
    return {
      k: 'finished',
      text,
      chip: (
        <span className="chip">
          <Icon name="flagCheckered" size="sm" />
          {text}
        </span>
      ),
    };
  }
  // Paused (Couriers): nothing can be sent to them until they're unpaused; said until they've been out.
  if (p.paused) {
    const text = p.pausedAt != null ? `Paused since ${dayWords(dayOf(p.pausedAt, m.zone), { weekday: true, short: true })}` : 'Paused';
    return {
      k: 'paused',
      text,
      chip: (
        <span className="chip">
          <Icon name="pauseCircle" size="sm" />
          {text}
        </span>
      ),
    };
  }
  // A route left open from an earlier day isn't today's, but it's said, as on the phone.
  const open = p.day?.leftOpen;
  if (open) return { k: 'notOut', text: openWords(m, open), chip: <span className="chip">{openWords(m, open)}</span> };
  return { k: 'notOut', text: 'Not out yet', chip: <span className="chip">Not out yet</span> };
}

/** A courier's numbers over the last [days] days (to today), as the stats engine counts them. */
export function courierTally(m: Model, uid: string, days: number): Tally {
  const t = noTally();
  for (let i = 0; i < days; i++) {
    const day = m.tallies.get(addDays(m.today, -i));
    const mine = day?.byCourier.get(uid);
    if (mine) addTally(t, mine);
  }
  return t;
}

const pct = (n: number, of: number) => {
  const r = rate(n, of);
  return r == null ? '–' : `${Math.round(r * 100)}%`;
};

const CTABS: [string, string, string][] = [
  ['all', 'Everyone', 'users'],
  ['running', 'On a route', 'path'],
  ['waiting', 'Waiting to start', 'circleDashed'],
  ['finished', 'Finished', 'flagCheckered'],
  ['notOut', 'Not out yet', 'minus'],
];

export function CouriersPage({ m }: { m: Model }) {
  const s = useStore(ui);
  const q = s.cq.trim().toLowerCase();
  // Paused counts every paused courier, whatever they're doing; the other tabs go by today.
  const inTab = (p: Person, k: string) => k === 'all' || (k === 'paused' ? p.paused : courierState(m, p).k === k);
  const rows = m.people.filter(
    (p) =>
      inTab(p, s.ctab) &&
      (!q || [p.name, p.ownName, p.courier.phone, vehicleWords(p.courier.vehicle), ...m.routes.filter((r) => r.riderId === p.uid).map((r) => routeTitle(r, m.zone))].join(' ').toLowerCase().includes(q) || phoneMatches(q, p.courier.phone, m.business?.country ?? null)),
  );
  return (
    <div className="tpage" id="couriersPage">
      <div className="filters">
        <div className="tabs">
          {CTABS.map(([k, l, i]) => (
            <button key={k} className="tab" aria-pressed={s.ctab === k} onClick={() => ui.set({ ctab: k })}>
              <Icon name={i} />
              {l}
              <span className="c">{m.people.filter((p) => inTab(p, k)).length}</span>
            </button>
          ))}
          <span className="vr" />
          <button className="tab" aria-pressed={s.ctab === 'zones'} data-tip="The areas each courier covers, on the map" onClick={() => ui.set({ ctab: 'zones', rec: null })}>
            <Icon name="polygon" />
            Zones
            <span className="c">{m.zones.length}</span>
          </button>
        </div>
        <span className="grow" />
        <button className="btn" onClick={() => openDialog({ kind: 'invite' })}>
          <Icon name="userPlus" />
          Invite a courier
        </button>
      </div>
      {s.ctab === 'zones' ? (
        <ZonesView m={m} />
      ) : (
      <div className="card">
        <div className="tools">
          <div className="field">
            <Icon name="magnifyingGlass" />
            <input id="courq" placeholder="Search name, phone or route" value={s.cq} onChange={(e) => ui.set({ cq: e.target.value })} autoComplete="off" spellCheck={false} aria-label="Search couriers" />
          </div>
          <span className="grow" />
          {/* Paused couriers, whatever they're doing today: only while there are some (or it's on). */}
          {(m.people.some((p) => p.paused) || s.ctab === 'paused') && (
            <button className="tab" aria-pressed={s.ctab === 'paused'} data-tip="Couriers nothing can be sent to until you unpause them" onClick={() => ui.set({ ctab: s.ctab === 'paused' ? 'all' : 'paused' })}>
              <Icon name="pauseCircle" />
              Paused
              <span className="c">{m.people.filter((p) => p.paused).length}</span>
            </button>
          )}
          <span className="cap">
            {rows.length} of {m.people.length}
          </span>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Courier</th>
                <th>Today</th>
                <th className="num">Stops today</th>
                <th className="num">
                  <span className="dotted" data-tip="Delivered the first time they went, the last 30 days. A closed shop, or a customer who asked for another day, isn’t counted.">
                    Delivered first time
                  </span>
                </th>
                <th className="num">
                  <span className="dotted" data-tip={`${m.business?.name ?? 'Your business'}’s money a courier holds since the last hand-in: cash, and transfers or POS into their own account. On call keeps the delivery fee; fees paid into your account, you owe them.`}>
                    To hand in
                  </span>
                </th>
                <th className="low">Joined</th>
                <th className="act" />
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((p) => {
                  const st = m.routes.filter((r) => r.riderId === p.uid && r.state !== 'waiting' && r.state !== 'declined').flatMap((r) => r.stops);
                  const t = courierTally(m, p.uid, 30);
                  return (
                    <tr key={p.uid} className={`r${s.rec?.type === 'courier' && s.rec.id === p.uid ? ' sel' : ''}`} data-ctx={`courier:${p.uid}`} onClick={() => openCourier(p.uid)}>
                      <td>
                        <span className="row">
                          <Av p={p} />
                          <span style={{ lineHeight: 1.25 }}>
                            <span className="addr">{p.name}</span>
                            <br />
                            <span className="area">
                              {vehicleWords(p.courier.vehicle)} · {p.onCall ? 'on call' : 'employed'}
                              {p.paused && courierState(m, p).k !== 'paused' ? ' · paused' : ''}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>{courierState(m, p).chip}</td>
                      <td className="num mono">{st.length ? `${st.filter(handled).length}/${st.length}` : '–'}</td>
                      <td className="num mono">{pct(t.firstTime, t.rated)}</td>
                      <td className="num mono">{handCell(m, p.balance, p.first)}</td>
                      <td className="ink2 low">{p.courier.joinedAt ? dayWords(new Intl.DateTimeFormat('en-CA', { timeZone: m.zone }).format(new Date(p.courier.joinedAt)), { weekday: false }) : '–'}</td>
                      <td className="act" onClick={(e) => e.stopPropagation()}>
                        <More menu={`courier:${p.uid}`} />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyHelp
                      q={s.cq.trim()}
                      title={m.people.length ? 'Nobody here right now.' : 'No couriers in your business yet.'}
                      tries={[
                        ...(m.people.length ? [{ icon: 'users', label: 'Everyone', note: 'Every courier, any status', act: 'all' }] : []),
                        { icon: 'userPlus', label: 'Invite a courier', note: 'A link they open in the Routr app', act: 'invite' },
                        ...(s.cq ? [{ icon: 'x', label: 'Clear the search', act: 'clear' }] : []),
                      ]}
                      onTry={(a) => (a === 'all' ? ui.set({ ctab: 'all', cq: '' }) : a === 'clear' ? ui.set({ cq: '' }) : openDialog({ kind: 'invite' }))}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

/** A courier's record, docked beside a table. */
export function CourierRec({ m, p }: { m: Model; p: Person }) {
  const week = courierTally(m, p.uid, 7);
  const month = courierTally(m, p.uid, 30);
  const routes = m.routes.filter((r) => r.riderId === p.uid);
  const events = routes
    .flatMap((r) => [
      r.sentAt != null ? { icon: 'paperPlaneTilt', w: `${routeTitle(r, m.zone)} sent`, at: r.sentAt } : null,
      r.seenAt != null ? { icon: 'eye', w: `Saw ${r.name ? `the ${r.name}` : 'the route'}`, at: r.seenAt } : null,
      r.startedAt != null ? { icon: 'path', w: `Started ${r.name ? `the ${r.name}` : 'a route'}`, at: r.startedAt } : null,
      ...r.stops
        .filter((s) => handled(s) && s.at != null)
        .map((s) => ({
          icon: s.state === 'done' ? 'checkCircle' : s.reason === 'anotherDay' ? 'calendarBlank' : 'xCircle',
          w: s.state === 'done' ? `Delivered${s.name ? ` to ${s.name}` : ''}` : s.reason === 'anotherDay' ? 'Asked for another day' : `Not delivered · ${reasonWords(s.reason)}`,
          at: s.at!,
          note: streetOf(s),
          stop: keyOf(s),
        })),
      (r.completedAt ?? r.endedAt) != null ? { icon: 'flagCheckered', w: `Finished ${r.name ? `the ${r.name}` : 'the route'}`, at: (r.completedAt ?? r.endedAt)! } : null,
    ])
    .filter((x): x is { icon: string; w: string; at: number; note?: string; stop?: string } => !!x)
    .sort((a, b) => b.at - a.at)
    .slice(0, 8);
  const biz = m.business?.name ?? 'Your business';
  const covers = m.zones.filter((z) => z.couriers.includes(p.uid));
  // Their profile (board `courier-profile-web`): the vehicle and pay as changed, while the change waits its 5 seconds.
  const { edits } = useStore(profileEdits);
  const vehicle = vehicleNow(p, edits);
  const onCall = onCallNow(p, edits);
  const paused = pausedNow(p, edits);
  const waitingTo = routes.filter((r) => r.state === 'waiting').length;
  // Their routes today and the last 60 days' (`courier-page`), newest first.
  const theirs = new Map<string, Route>();
  for (const r of [...routes, ...(p.day?.finished ?? []), ...(m.history ?? [])]) if (r.riderId === p.uid && r.startedAt != null && !theirs.has(r.id)) theirs.set(r.id, r);
  const recent = [...theirs.values()].sort((a, b) => b.startedAt! - a.startedAt!).slice(0, 5);
  // How late the late ones were, the same 7 days: stops delivered after their deliver-by time.
  const from = addDays(m.today, -6);
  const timed = [...theirs.values()].flatMap((r) => r.stops).filter((s) => s.state === 'done' && s.at != null && s.deliverBy != null && dayOf(s.at, m.zone) >= from);
  const late = timed.filter((s) => s.at! > s.deliverBy!);
  const lateMin = late.length ? Math.round(late.reduce((n, s) => n + (s.at! - s.deliverBy!), 0) / late.length / 60000) : 0;
  return (
    <>
      <div className="rec-h">
        <Av p={p} size="xl" />
        <div className="grow">
          <div className="title ellipsis">{p.name}</div>
          {p.shortName && <div className="cap ellipsis">{p.ownName} in their profile</div>}
          <div className="cap">
            {p.courier.phone ? (
              <a className="mono" href={`tel:${p.courier.phone}`} data-tip={`Call ${p.first}`}>
                {p.courier.phone}
              </a>
            ) : p.courier.email ? (
              <a href={`mailto:${p.courier.email}`}>{p.courier.email}</a>
            ) : (
              'No phone or email in their profile'
            )}
          </div>
          {p.courier.joinedAt != null && <div className="cap">Joined {dayWords(dayOf(p.courier.joinedAt, m.zone), { weekday: false })}</div>}
          {covers.length > 0 && (
            <button
              className="link cap"
              style={{ display: 'block', textAlign: 'left' }}
              data-tip="Open Zones"
              onClick={() => {
                openZones(covers[0].id);
                track('courier_zones_opened', { zones: covers.length });
              }}
            >
              Covers {covers.map((z) => z.name).join(', ')}
            </button>
          )}
        </div>
        <button className="iconbtn sm" data-menu={`courier:${p.uid}`} data-tip="Courier actions" aria-label="Courier actions">
          <Icon name="dotsThreeVertical" />
        </button>
        <button className="iconbtn sm" data-tip="Close · Esc" aria-label="Close" onClick={() => ui.set({ rec: null })}>
          <Icon name="x" />
        </button>
      </div>
      <div className="rec-b">
        {courierState(m, p).chip}
        <div className="big3" style={{ marginTop: 16 }}>
          <div>
            <div className="v">{pct(month.firstTime, month.rated)}</div>
            <div className="l">delivered first time</div>
          </div>
          <div>
            <div className="v">{pct(month.onTimeMet, month.onTimeOf)}</div>
            <div className="l">on time</div>
          </div>
          <div>
            <div className="v">{week.delivered}</div>
            <div className="l">delivered, 7 days</div>
          </div>
        </div>
        <div className="cap">The last 30 days, and the last 7 for deliveries.</div>
        <div className="group">
          <div className="eyebrow">The last 7 days</div>
          <p className="prose">
            {week.delivered + week.notDelivered === 0
              ? `${p.first} hasn’t delivered anything in the last 7 days.`
              : `${plural(week.delivered, 'stop')} delivered, ${week.firstTime} of them the first time${week.onTimeOf ? ` and ${week.onTimeMet} of ${week.onTimeOf} by their deliver-by time` : ''}${week.km ? `, over ${length(week.km * 1000)}` : ''}.`}
            {m.history != null && timed.length > 0 && (late.length ? ` The ${plural(late.length, 'late one')} ${late.length === 1 ? 'was' : 'were'} about ${plural(lateMin, 'minute')} late${late.length > 1 ? ' on average' : ''}.` : ' None was late.')}
          </p>
        </div>
        <div className="group">
          <div className="eyebrow">Profile</div>
          <div className="prow">
            <span>Vehicle</span>
            <button className="ctl sm" data-menu={`vehicle:${p.uid}`} data-tip="What they carry and their marker go by it">
              {vehicleWords(vehicle)}
              <Icon name="caretDown" size="sm" />
            </button>
          </div>
          <div className="prow">
            <span>On call or employed</span>
            <button className="ctl sm" data-menu={`pay:${p.uid}`}>
              {onCall ? 'On call' : 'Employed'}
              <Icon name="caretDown" size="sm" />
            </button>
          </div>
          <div className="prow">
            <span>Colour</span>
            <ColourRow m={m} p={p} />
          </div>
          <div className="prow">
            <span>Short name</span>
            <button className="ctl sm" data-tip="What your business calls them; customers still see their own name" onClick={() => openDialog({ kind: 'shortName', riderId: p.uid })}>
              {p.shortName ?? 'None'}
              <Icon name="notePencil" size="sm" />
            </button>
          </div>
          <div className="prow">
            <span>Paused</span>
            <button
              className={`ctl sm${paused ? ' on' : ''}`}
              aria-pressed={paused}
              data-tip={paused ? `Unpause ${p.first}: routes and stops can go to them again` : `Off Plan, Share out and Try again; nothing can be sent to ${p.first}`}
              onClick={() => act.setPaused(p.uid, !paused)}
            >
              <Icon name={paused ? 'playCircle' : 'pauseCircle'} size="sm" />
              {paused ? `Unpause ${p.first}` : `Pause ${p.first}`}
            </button>
          </div>
          {paused && (
            <div className="cap" style={{ marginTop: 6 }}>
              {p.first} is off Plan, Share out and Try again, and nothing can be sent to them; their phone says so. Their routes and numbers stay.
              {waitingTo ? ` ${plural(waitingTo, 'route')} sent before still ${waitingTo === 1 ? 'waits' : 'wait'} to start: take ${waitingTo === 1 ? 'it' : 'them'} back or give ${waitingTo === 1 ? 'it' : 'them'} to someone else.` : ''}
            </div>
          )}
          <div className="cap" style={{ marginTop: 10 }}>
            Only {p.ownFirst} can change their own name and photo, in the Routr app.
          </div>
        </div>
        <div className="group">
          <div className="eyebrow">{p.balance < 0 ? 'Settling up' : 'To hand in'}</div>
          <div className="row" style={{ marginTop: 6 }}>
            <b className="mono grow" style={{ fontSize: 18 }}>
              {p.balance < 0 ? `You owe ${p.first} ${money(-p.balance, m.currency)}` : money(p.balance, m.currency)}
            </b>
            {(p.balance > 0 || p.waitingHandIns.length > 0) && (
              <button className="btn sm strong" onClick={() => openDialog({ kind: 'handIn', riderId: p.uid, handInId: p.waitingHandIns[0]?.id })}>
                {p.waitingHandIns.length ? 'Confirm their hand-in' : 'Record a hand-in'}
              </button>
            )}
            {p.balance < 0 && !p.waitingHandIns.length && (
              <button className="btn sm" onClick={() => openDialog({ kind: 'handIn', riderId: p.uid, out: true })}>
                Record paying {p.first}
              </button>
            )}
          </div>
          <div className="cap" style={{ marginTop: 4 }}>
            {biz}’s money {p.first} holds since the last hand-in: cash, and transfers or POS into {p.first}’s own account.{' '}
            {p.onCall ? 'On call keeps the delivery fee; fees paid into your account, you owe them.' : 'Employed: hands in everything taken.'}
            {p.onRoute > 0 && ` Plus ${money(p.onRoute, m.currency)} on the route ${p.first} is on now, counted when it ends.`}
          </div>
          {p.waitingHandIns.map((h) => (
            <div key={h.id} className="row cap" style={{ marginTop: 8, gap: 6 }}>
              <Icon name="money" size="sm" />
              Says they handed in {money(h.amount, m.currency)}
              {h.at ? `, ${ago(h.at, m.now)}` : ''}: waiting for you to confirm.
            </div>
          ))}
        </div>
        <div className="group">
          <div className="eyebrow">Today</div>
          {events.length ? (
            <div className="tl">
              {events.map((x, i) => (
                <div
                  key={i}
                  className={x.stop ? 'link-row' : ''}
                  role={x.stop ? 'button' : undefined}
                  tabIndex={x.stop ? 0 : undefined}
                  onClick={x.stop ? () => openStop(x.stop!) : undefined}
                >
                  <Icon name={x.icon} size="sm" />
                  <span>
                    {x.w}
                    {x.note && <span className="ink2"> · {x.note}</span>}
                  </span>
                  <span className="mono ink2" style={{ fontSize: 12 }}>
                    {time(x.at, m.zone)} · {ago(x.at, m.now)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="cap" style={{ marginTop: 6 }}>
              Nothing yet today.
            </div>
          )}
        </div>
        <div className="group">
          <div className="eyebrow">Recent routes</div>
          {recent.length ? (
            <div className="tl">
              {recent.map((r) => (
                <div key={r.id} className="link-row" role="button" tabIndex={0} data-ctx={`route:${r.id}`} onClick={() => openRoute(r.id)} onKeyDown={(e) => e.key === 'Enter' && openRoute(r.id)}>
                  <Icon name="path" size="sm" />
                  <span>
                    {routeTitle(r, m.zone)}
                    <span className="ink2">
                      {' '}
                      · {r.stops.filter((s) => s.state === 'done').length} of {plural(r.stops.length, 'stop')} delivered
                    </span>
                  </span>
                  <span className="mono ink2" style={{ fontSize: 12 }}>
                    {r.state === 'running' ? 'Now' : dayWords(dayOf(r.startedAt!, m.zone), { weekday: true, short: true })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="cap" style={{ marginTop: 6 }}>
              {m.history == null ? 'Reading their routes…' : 'Not out yet'}
            </div>
          )}
        </div>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 22 }}>
          {routes.length > 0 && (
            <button className="btn sm" onClick={() => openPerson(p.uid, routeOfPerson(m))}>
              <Icon name="path" size="sm" />
              Open the route
            </button>
          )}
          {!paused && (
            <button className="btn sm" onClick={() => planWith([], p.uid)}>
              <Icon name="paperPlaneTilt" size="sm" />
              Plan a route for {p.first}
            </button>
          )}
          {p.courier.phone && (
            <a className="btn sm" href={`tel:${p.courier.phone}`}>
              <Icon name="phone" size="sm" />
              Call
            </a>
          )}
        </div>
        <div className="cap" style={{ marginTop: 14 }}>
          {plural(month.delivered, 'stop')} delivered in 30 days
          {month.notDelivered ? `, ${month.notDelivered} not` : ''}.
        </div>
      </div>
    </>
  );
}

/**
 * A courier's colour, every choice in view (Spoke's is a dropdown of names): Automatic first, then the six, never green,
 * red or amber. The current one ticked; one someone else has shows their initial, and its tip says who. One click picks.
 */
function ColourRow({ m, p }: { m: Model; p: Person }) {
  return (
    <div className="swatches" role="group" aria-label={`${p.first}’s colour: ${colourWords(p)}`}>
      {colourChoices(m, p.uid).map((c) => (
        <button
          key={c.value ?? 'auto'}
          className={`sw${c.value == null ? ' auto' : ''}`}
          aria-pressed={c.picked}
          aria-label={c.label}
          data-tip={c.label}
          style={{ '--c': c.hex, '--on': c.on } as React.CSSProperties}
          onClick={() => act.changeColour(p.uid, c.value)}
        >
          {c.picked ? <Icon name="check" size="sm" /> : c.holders[0] ? c.holders[0].first.slice(0, 1).toUpperCase() : null}
        </button>
      ))}
    </div>
  );
}
